import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Instagram, Tiktok, Youtube } from 'react-bootstrap-icons'
import axiosService from '../../../utils/AxiosService'
import { providerLogo } from '../../../utils/Helper'
import PostsTimeline from '../Shared/PostsTimeline'

export default function BrandOverview() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [brand, setBrand] = useState(null)
  const [posts, setPosts] = useState([])
  const [loadingPosts, setLoadingPosts] = useState(true)
  const [activeProvider, setActiveProvider] = useState(null)

  useEffect(() => {
    axiosService
      .get(`/api/project_manager/brands/${id}`)
      .then((data) => setBrand(data))
      .catch(() => setBrand(null))
  }, [id])

  useEffect(() => {
    setLoadingPosts(true)
    axiosService
      .get(`/api/project_manager/brands/${id}/posts`)
      .then((data) => {
        console.log('data:',data)
        setPosts(data || [])
      })
      .catch(() => {
        setPosts([])
      })
      .finally(() => setLoadingPosts(false))
  }, [id])


  const providerOptions = [
    { key: 'instagram', label: 'Instagram', Icon: Instagram },
    { key: 'tiktok', label: 'TikTok', Icon: Tiktok },
    { key: 'youtube', label: 'Youtube', Icon: Youtube },
  ]

  // Provider ikonok mapping - kis ikonokhoz
  const getProviderIcon = (provider) => {
    const iconMap = {
      instagram: { Icon: Instagram, color: 'text-pink-600' },
      tiktok: { Icon: Tiktok, color: '' },
      youtube: { Icon: Youtube, color: 'text-red-500' },
    }
    return iconMap[provider]
  }

  // Provider színek - ugyanaz, mint a Providers komponensben
  const getProviderColor = (provider) => {
    const colorMap = {
      instagram: 'text-pink-600',
      tiktok: '',
      youtube: 'text-red-500',
      linkedin: 'text-rb-linkedin'
    }
    return colorMap[provider] || ''
  }

  return (
    <div className="pt-20 px-8 pb-10 bg-rb-bg dark:bg-rb-dark-mode-background min-h-screen">
      {/* Felső navigációs sor */}
      <div className="flex items-center mb-3 text-xs text-gray-500 dark:text-gray-400">
        <button
          onClick={() => navigate('/project_manager/brands')}
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mr-2"
        >
          ← Back to brands
        </button>
      </div>

      {/* Felső header – brand adatok + platform linkek + provider váltó (Figma layout alapján) */}
      <div className="bg-white dark:bg-rb-dark-mode rounded-2xl shadow-sm border border-rb-widget-border dark:border-rb-dark-mode-lighter px-6 py-5 mb-4">
        <div className="flex flex-col gap-4">
          {/* Logo + név + actions */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              {/* Logo – 3 sor magas blokk */}
              {brand?.logo_url ? (
                <img
                  src={brand.logo_url}
                  alt={brand.name || 'Brand logo'}
                  className="h-16 w-16 rounded-md object-cover bg-gray-100 dark:bg-rb-dark-mode-lighter flex-shrink-0 border border-solid border-gray-600 dark:border-rb-dark-mode-lighter"
                />
              ) : (
                <div className="h-16 w-16 rounded-md bg-red-100 dark:bg-rb-dark-mode-lighter flex items-center justify-center text-xs font-bold text-red-600 dark:text-gray-400 flex-shrink-0 border border-solid border-gray-600 dark:border-rb-dark-mode-lighter">
                  {brand?.name ? (brand.name[0] || '').toUpperCase() : 'BR'}
                </div>
              )}
              <div className="flex-1">
                <h1 className="text-lg font-semibold leading-tight dark:text-white">{brand?.name || `Brand #${id}`}</h1>
                {brand?.domain && (
                  <a
                    href={`https://${brand.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gray-500 dark:text-gray-400 hover:text-rb-green dark:hover:text-rb-green hover:underline block mb-0.5"
                  >
                    {brand.domain}
                  </a>
                )}
                {/* Provider ikonok - harmadik sor */}
                <div className="mt-2 flex items-center gap-2">
                  {(brand?.providers || []).slice(0, 3).map((provider) => {
                    const providerData = getProviderIcon(provider)
                    if (!providerData) return null
                    const { Icon, color } = providerData
                    return (
                      <span
                        key={provider}
                        className={`h-4 w-4 flex items-center justify-center ${color} ${color === '' ? 'dark:text-white' : ''} ${color === 'text-pink-600' ? 'dark:text-pink-400' : ''} ${color === 'text-red-500' ? 'dark:text-red-400' : ''}`}
                        title={provider}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                    )
                  })}
                  {(!brand?.providers || brand.providers.length === 0) && (
                    <span className="h-4 w-4 rounded-full bg-gray-200 dark:bg-rb-dark-mode-lighter" />
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button className="bg-rb-green text-white dark:text-rb-dark-mode rounded-md px-3 py-3 text-xs font-semibold hover:bg-opacity-90">
                Edit brand
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Provider selector panel - külön panel a brand adat panel alatt */}
      <div className="bg-white dark:bg-rb-dark-mode rounded-2xl shadow-sm border border-rb-widget-border dark:border-rb-dark-mode-lighter px-6 py-4 mb-4">
        <div className="flex items-center">
          {/* All gomb */}
          <button
            type="button"
            onClick={() => setActiveProvider(null)}
            className={`flex items-center p-3 rounded-2xl border-1 ${
              activeProvider === null
                ? 'bg-rb-soft-green dark:bg-rb-green/20 border-rb-green dark:border-rb-green'
                : 'bg-rb-grey dark:bg-rb-dark-mode-lighter border-widget-border dark:border-rb-dark-mode-lighter hover:bg-gray-100 dark:hover:bg-rb-dark-mode'
            } dark:text-white`}
          >
            <span className="mx-4">All</span>
          </button>
          {(brand?.providers || []).map((provider) => {
            const isActive = activeProvider === provider
            return (
              <button
                key={provider}
                type="button"
                onClick={() => setActiveProvider(provider)}
                className={`flex items-center p-3 rounded-2xl ml-4 border-1 ${
                  isActive
                    ? 'bg-rb-soft-green dark:bg-rb-green/20 border-rb-green dark:border-rb-green'
                    : 'bg-rb-grey dark:bg-rb-dark-mode-lighter border-widget-border dark:border-rb-dark-mode-lighter hover:bg-gray-100 dark:hover:bg-rb-dark-mode'
                }`}
              >
                <span className={`${getProviderColor(provider)} ${provider === 'instagram' ? 'dark:text-pink-400' : ''} ${provider === 'youtube' ? 'dark:text-red-400' : ''} ${provider === 'tiktok' ? 'dark:text-white' : ''}`}>
                  {providerLogo(provider)}
                </span>
                <span className="ml-2 capitalize dark:text-white">{provider}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Fő tartalom rész – bal: timeline + metrikák, jobb: brand részletek */}
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1">
          <PostsTimeline
            posts={posts}
            groupBy="authentication"
            loading={loadingPosts}
            activeProvider={activeProvider}
          />
        </div>
      </div>
    </div>
  )
}
